import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { mockOffers } from '@/mocks/offers';
import { mockPartners } from '@/mocks/partners';
import { mockVerticals } from '@/mocks/verticals';
import { mockGeos } from '@/mocks/geos';
import BaseSelect from '@/components/base/BaseSelect';
import { ArrowLeft, Save } from 'lucide-react';
import Checkbox from '@/components/base/Checkbox';

export default function OfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const offer = useMemo(() => isNew ? null : mockOffers.find(o => o.id === id), [id, isNew]);

  if (!isNew && !offer) {
    return (
      <div className="text-center py-12">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Offer not found</p>
        <button
          onClick={() => navigate('/offers')}
          className="mt-2 text-sm transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          Back to offers
        </button>
      </div>
    );
  }

  const partner = mockPartners.find(p => p.id === offer?.partner_id);
  const vertical = mockVerticals.find(v => v.id === offer?.vertical_id);
  const geo = mockGeos.find(g => g.id === offer?.geo_id);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/offers')}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          {isNew ? 'New Offer' : `${partner?.display_name} — ${vertical?.name}`}
        </h1>
      </div>

      <div className="rounded-xl border p-5 space-y-6" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Partner</label>
            <BaseSelect
              value={partner?.id || ''}
              onChange={() => {}}
              options={mockPartners.map(p => ({ value: p.id, label: p.display_name }))}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Vertical</label>
            <BaseSelect
              value={vertical?.id || ''}
              onChange={() => {}}
              options={mockVerticals.map(v => ({ value: v.id, label: v.name }))}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Geo</label>
            <BaseSelect
              value={geo?.id || ''}
              onChange={() => {}}
              options={mockGeos.map(g => ({ value: g.id, label: g.name }))}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider mb-2 block" style={{ color: 'var(--text-muted)' }}>Payout Model</label>
            <BaseSelect
              value={offer?.payout_model || 'cpl'}
              onChange={() => {}}
              options={[
                { value: 'cpl', label: 'CPL — Cost Per Lead' },
                { value: 'cpa', label: 'CPA — Cost Per Acquisition' },
                { value: 'revshare', label: 'RevShare — Revenue Share' },
                { value: 'flat_subscription', label: 'Flat Subscription' },
              ]}
            />
          </div>
        </div>

        {/* Payout config */}
        <div className="border-t pt-5" style={{ borderColor: 'var(--border-separator)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Payout Configuration</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-muted)' }}>Rate (local)</label>
              <input type="number" defaultValue={offer?.payout_config.rate_local || ''} className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-muted)' }}>Currency</label>
              <BaseSelect
                value={offer?.payout_config.currency || 'EUR'}
                onChange={() => {}}
                options={[{ value: 'EUR', label: 'EUR' }, { value: 'USD', label: 'USD' }]}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: 'var(--text-muted)' }}>Priority</label>
              <input type="number" defaultValue={offer?.priority || 50} className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }} />
            </div>
          </div>
        </div>

        {/* Caps */}
        <div className="border-t pt-5" style={{ borderColor: 'var(--border-separator)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Caps</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {['daily', 'weekly', 'monthly'].map(cap => (
              <div key={cap}>
                <label className="text-[10px] uppercase tracking-wider mb-1 block capitalize" style={{ color: 'var(--text-muted)' }}>{cap} cap</label>
                <input type="number" defaultValue={(offer?.caps as Record<string, number | null>)?.[cap] || ''} placeholder="Unlimited" className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Filter rules */}
        <div className="border-t pt-5" style={{ borderColor: 'var(--border-separator)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Filter Rules</h3>
          <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>Configure based on vertical requirements</p>
          <pre className="rounded-xl p-4 text-xs overflow-x-auto font-mono border" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}>
            {JSON.stringify(offer?.filter_rules || {}, null, 2)}
          </pre>
        </div>

        {/* Schedule */}
        <div className="border-t pt-5" style={{ borderColor: 'var(--border-separator)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Schedule</h3>
          <div className="flex items-center gap-2">
            <Checkbox checked={offer?.schedule.always_on !== false} onChange={() => {}} />
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Always on</span>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--border-separator)' }}>
          <button className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl transition-all" style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}>
            <Save size={14} />
            Save Offer
          </button>
        </div>
      </div>
    </div>
  );
}
